import { useState, useMemo } from "react";
import type * as React from "react";

// Third Party
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import calculateDaysFromNow from "@/utils/calculateDaysFromNow";
import { RequireEditDocs } from "@/services/require-edit-docs";

// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { FileUpload } from "@/components/FileUpload";
import DatePicker from "@/components/ui/DatePicker";
import { Spinner } from "@/components/ui/spinner";

import { Input } from "@/components/ui/input";
import DinamixSkeletonTable from "../skeleton/DinamixTableSkeleton";
import { toast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import NumberFormatSelector from "../NumberFormatSelector";

// Icons
import { CirclePlus, PencilLine, Eye } from "lucide-react";

import { postDivPay } from "@/services/tax-obligations";

import FileHandleViewer from "@/components/FileHandleViewer";

import { ModalRequireEditDoc } from "@/components/ModalRequireEditDoc";

interface TaxObligation {
  id: string;
  companyId: string;
  obligationType: string;
  yearly: number;
  profitTax: number;
  addIncomeTax: number;
  monthlyTax: number;
  valueAddedTax: number;
  exciseTax: number;
  dividendTax: number;
  otherTaxes: number;
  totalTaxes: number;
  docFile: string;
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  // Additional fields for comparison data
  currentExec: number;
  currentPlan: number;
  currentDiff: number;
  currentDiffPct: number;
  lastExec: number;
  lastDiff: number;
  lastDiffPct: number;
}

interface TaxObligationResponse {
  status: boolean;
  message: string;
  total: number;
  data: TaxObligation[];
}

interface TaxLiabilityPlanProps {
  data?: TaxObligationResponse;
  onYearChange?: (year: string) => void;
  onStartYearChange?: (year: string) => void;
  onEndYearChange?: (year: string) => void;
  isloading?: boolean;
  yearly?: string;
  startYear?: number;
  endYear?: number;
}

interface DividendData {
  id: string;
  companyId: string;
  yearly: number;
  plannedDividend: number;
  totalPhase: number;
  actualDividend: number;
  phase: number;
  docFile: string;
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

interface DividendResponse {
  status: boolean;
  message: string;
  total: number;
  data: DividendData[];
}

interface DividendPlanProps {
  data?: DividendResponse;
  onYearChange?: (year: string) => void;
  onStartYearChange?: (year: string) => void;
  onEndYearChange?: (year: string) => void;
  isloading?: boolean;
  yearly?: string;
  startYear?: number;
  endYear?: number;
}

// First Functional Component
const AssignTaxObligations: React.FC<TaxLiabilityPlanProps> = ({
  data,
  onStartYearChange,
  onEndYearChange,
  isloading,
  startYear,
  endYear,
}) => {
  const navigate = useNavigate();
  const [unit, setUnit] = useState("1");
  const currentYear = new Date().getFullYear();
  const { delegationReport } = useAccessPermission();

  const yearOptions = useMemo(() => {
    return [...Array(currentYear - 1975 + 1)].map((_, index) =>
      (currentYear - index).toString()
    );
  }, [currentYear]);

  const endYearOptions = useMemo(() => {
    if (!startYear || startYear === 0) {
      return yearOptions;
    }
    const startYearIndex = yearOptions.findIndex(
      (year) => Number.parseInt(year) === startYear
    );
    if (startYearIndex === -1) return yearOptions;
    return yearOptions.slice(0, startYearIndex + 1);
  }, [startYear, yearOptions]);

  return (
    <>
      <div className="flex flex-wrap items-end justify-between mb-2 border-none shadow-none gap-x-2 bg-none">
        <div className="flex items-center w-9/12 gap-x-2">
          <div className="flex flex-col gap-y-1">
            <Label htmlFor="">ປີເລີ່ມຕົ້ນ</Label>
            <Select
              value={startYear?.toString()}
              onValueChange={onStartYearChange}
            >
              <SelectTrigger className="w-[150px] indent-1">
                <SelectValue placeholder="ເລືອກປີ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">ທັງໝົດ</SelectItem>
                {yearOptions.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-y-1">
            <Label htmlFor="">ປີສິ້ນສຸດ</Label>
            <Select
              value={endYear?.toString()}
              onValueChange={onEndYearChange}
              disabled={!startYear || startYear === 0}
            >
              <SelectTrigger className="w-[150px] indent-1">
                <SelectValue placeholder="ເລືອກປີ" />
              </SelectTrigger>
              <SelectContent>
                {endYearOptions.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <NumberFormatSelector unit={unit} setUnit={setUnit} />
        </div>
      </div>
      {isloading ? (
        <DinamixSkeletonTable rows={8} columns={5} />
      ) : (
        <Table className="border">
          <TableHeader>
            <TableRow className="[&>*]:whitespace-nowrap">
              <TableHead className="w-[60px] ">ລຳດັບ</TableHead>
              <TableHead>ປະຈໍາປີ</TableHead>
              <TableHead>ຕົວຈິງຂອງ ປີນີ້</TableHead>
              <TableHead>ແຜນການຂອງ ປີນີ້</TableHead>
              <TableHead>ປ່ຽນແປງທຽບໃສ່ແຜນ</TableHead>
              <TableHead>ທຽບໃສ່ແຜນຂອງປີນີ້</TableHead>
              <TableHead>ຕົວຈິງຂອງປີກ່ອນ</TableHead>
              <TableHead>ປ່ຽນແປງທຽບໃສ່ແຜນ</TableHead>
              <TableHead>ທຽບໃສ່ ຂອງປີກ່ອນ</TableHead>

              <TableHead>ເອກະສານຕິດຄັດ</TableHead>
              {delegationReport.isUpdate && <TableHead>ຈັດການ</TableHead>}
            </TableRow>
          </TableHeader>

          <TableBody className="[&>*]:whitespace-nowrap">
            {data?.data.map((item, index) => (
              <TableRow
                key={item.id}
                className="cursor-pointer"
                onClick={() => {
                  localStorage.setItem("taxId", String(item.id));
                  localStorage.setItem("yearly", String(item.yearly));
                  navigate({
                    to: "/report/obligation/assign-tax-obligation",
                    params: { id: item.id },
                  });
                }}
              >
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <div className="flex gap-x-2 text-clip">
                    <Eye size={16} color="#007AFF" />
                    {item.yearly}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {(item.currentExec / Number(unit)).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {(item.currentPlan / Number(unit)).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {(item.currentDiff / Number(unit)).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {item.currentDiffPct?.toFixed(2)}%
                </TableCell>
                <TableCell className="text-right">
                  {(item.lastExec / Number(unit)).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {(item.lastDiff / Number(unit)).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {item.lastDiffPct?.toFixed(2) || "-"}%
                </TableCell>

                <TableCell className="text-blue-500 underline">
                  {item.docFile
                    ? delegationReport.isDownload && (
                      <FileHandleViewer
                        fileUrl={item.docFile}
                        showBtnPreview={true}
                        text="ເບິ່ງເອກະສານ"
                      />
                    )
                    : "-"}
                </TableCell>
                {delegationReport.isUpdate && (
                  <TableCell
                    className="text-blue-500 underline"
                    data-edit-cell="true" // Add this attribute
                    onClick={(e) => e.stopPropagation()}
                  >
                    <AssignTaxObligationsEdit
                      companyId={item.id}
                      yearly={item.yearly}
                      currentExec={item.currentExec}
                      dateUpdated={item.updatedAt}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
};

import type { FC } from "react";

const Dividends: React.FC<DividendPlanProps> = ({
  data,
  onStartYearChange,
  onEndYearChange,
  isloading,
  startYear,
  endYear,
}) => {
  const navigate = useNavigate();
  const [unit, setUnit] = useState("1");
  const { delegationReport } = useAccessPermission();
  const currentYear = new Date().getFullYear();

  const yearOptions = useMemo(() => {
    return [...Array(currentYear - 1975 + 1)].map((_, index) =>
      (currentYear - index).toString()
    );
  }, [currentYear]);

  const endYearOptions = useMemo(() => {
    if (!startYear || startYear === 0) {
      return yearOptions;
    }
    const startYearIndex = yearOptions.findIndex(
      (year) => Number.parseInt(year) === startYear
    );
    if (startYearIndex === -1) return yearOptions;
    return yearOptions.slice(0, startYearIndex + 1);
  }, [startYear, yearOptions]);

  //   console.log("Dividend Data", data);

  return (
    <>
      <div className="flex flex-wrap items-end justify-between mb-2 border-none shadow-none gap-x-2 bg-none">
        <div className="flex items-center w-9/12 gap-x-2">
          <div className="flex flex-col gap-y-1">
            <Label htmlFor="">ປີເລີ່ມຕົ້ນ</Label>
            <Select
              value={startYear?.toString()}
              onValueChange={onStartYearChange}
            >
              <SelectTrigger className="w-[150px] indent-1">
                <SelectValue placeholder="ເລືອກປີ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">ທັງໝົດ</SelectItem>
                {yearOptions.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-y-1">
            <Label htmlFor="">ປີສິ້ນສຸດ</Label>
            <Select
              value={endYear?.toString()}
              onValueChange={onEndYearChange}
              disabled={!startYear || startYear === 0}
            >
              <SelectTrigger className="w-[150px] indent-1">
                <SelectValue placeholder="ເລືອກປີ" />
              </SelectTrigger>
              <SelectContent>
                {endYearOptions.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <NumberFormatSelector unit={unit} setUnit={setUnit} />
        </div>
      </div>
      {isloading ? (
        <DinamixSkeletonTable rows={8} columns={5} />
      ) : (
        <Table className="border">
          <TableHeader>
            <TableRow className="[&>*]:whitespace-nowrap ">
              <TableHead className="w-[60px] ">ລຳດັບ</TableHead>
              <TableHead>ປະຈໍາປີ</TableHead>
              <TableHead>ແຜນມອບເງິນ</TableHead>
              <TableHead>ຈຳນວນງວດ</TableHead>
              <TableHead>ມອບຕົວຈິງ</TableHead>
              <TableHead>ຈຳນວນງວດຕົວຈິງ</TableHead>
              <TableHead>ຜິດດ່ຽງຈາກແຜນ</TableHead>
              <TableHead>ສົມທຽບແຜນ (%)</TableHead>
              <TableHead>ເອກະສານຕິດຄັດ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="[&>*]:whitespace-nowrap">
            {data?.data.map((item, index) => (
              <TableRow
                key={item.id}
                className="cursor-pointer"
                onClick={() => {
                  localStorage.setItem("yearly", String(item.yearly));
                  localStorage.setItem("divId", String(item.id));
                  navigate({
                    to: "/report/obligation/dividend",
                    params: { id: item.id },
                  });
                }}
              >
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <div className="flex gap-x-2 text-clip">
                    <Eye size={16} color="#007AFF" />
                    {item.yearly}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {(item?.plannedDividend
                    ? item?.plannedDividend / Number(unit)
                    : 0
                  ).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {item.totalPhase || 0}
                </TableCell>
                <TableCell className="text-right">
                  {(item.actualDividend
                    ? item?.actualDividend / Number(unit)
                    : 0
                  ).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">{item.phase || 0}</TableCell>
                <TableCell className="text-right">
                  {(() => {
                    const difference =
                      item?.actualDividend - item.plannedDividend
                        ? (item?.actualDividend - item.plannedDividend) /
                        Number(unit)
                        : 0;

                    const isPositive = difference > 0;

                    return (
                      <span
                        className={`font-medium ${isPositive ? "text-green-600" : "text-red-600"
                          }`}
                      >
                        {difference.toLocaleString()}
                      </span>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  {(() => {
                    const percentage =
                      item.plannedDividend > 0
                        ? (item.actualDividend / item.plannedDividend) * 100
                        : 0;

                    const isOverPlan = percentage >= 100;

                    return (
                      <span
                        className={`font-medium ${isOverPlan ? "text-green-600" : "text-red-600"
                          }`}
                      >
                        {percentage.toFixed(2)}%
                      </span>
                    );
                  })()}
                </TableCell>
                <TableCell className="text-blue-500 underline">
                  {item.docFile
                    ? delegationReport.isDownload && (
                      <FileHandleViewer
                        fileUrl={item.docFile}
                        showBtnPreview={true}
                        text="ເບິ່ງເອກະສານ"
                      />
                    )
                    : "-"}
                </TableCell>
                {delegationReport.isUpdate && (
                  <TableCell
                    className="text-blue-500 underline"
                    data-edit-cell="true" // Add this attribute
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DividendPopup term={item.phase + 1} divId={item.id} />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
};

const DividendPopup: FC<EditInfoProps> = ({ term, divId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [open, setOpen] = useState(false);

  return (
    <div>
      <Button
        className="flex items-center border border-blue-500 bg-none gap-x-1 w-[120px] rounded-md p-2 text-blue-500"
        onClick={() => {
          localStorage.setItem("divId", divId.toString());
          setIsOpen(true);
        }}
        variant="secondary"
      >
        <CirclePlus size={16} /> ເພີ່ມລາຍງານ
      </Button>
      {isOpen && (
        <>

          <DividendsEdit
            open={isOpen}
            onClose={() => setIsOpen(false)}
            term={term}
            divId={divId}
          />

          <Button
            className="flex items-center border border-blue-500 bg-none gap-x-1 w-[120px] rounded-md p-2 text-blue-500"
            onClick={() => {
              setOpen(true);
            }}
            variant="secondary"
          >
            <PencilLine size={16} /> ຂໍແກ້ໄຂເອກະສານ
          </Button>

          <ModalRequireEditDoc
            isOpen={open}
            onClose={() => setOpen(false)}
            onConfirm={({ reason, file }) => {
              console.log("reason", reason);
              console.log("file", file);
            }}
          />
        </>
      )}
    </div>
  );
};

const DividendsEdit: FC<EditInfoProps> = ({ open, onClose, term }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    dividendPlanId: localStorage.getItem("divId") || "",
    dividend: "",
    dividendDate: "",
    docFile: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDividendChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Remove all commas first
    value = value.replace(/,/g, "");

    // Allow only numbers and one decimal point
    if (!/^\d*\.?\d{0,2}$/.test(value) && value !== "") {
      return;
    }

    // Format with commas if there's a value
    if (value) {
      const parts = value.split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      value = parts.join(".");
    }

    setFormData((prev) => ({ ...prev, dividend: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.dividend || !formData.dividendDate || !formData.docFile) {
      toast({
        title: "ຜິດພາດ",
        description: "ກະລຸນາປ້ອນຂໍ້ມູນໃຫ້ຄົບຖ້ວນ",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const data = {
        dividendPlanId: formData.dividendPlanId,
        dividend: Number(formData.dividend.replace(/,/g, "")),
        dividendDate: formData.dividendDate,
        docFile: formData.docFile,
      };

      await postDivPay(data);
      toast({
        title: "ສຳເລັດ",
        description: "ບັນທຶກຂໍ້ມູນສຳເລັດ",
        variant: "success",
      });

      queryClient.invalidateQueries({ queryKey: ["dividends"] });
      navigate({
        to: "/report/obligation",
        search: { active: "dividends" as const, startYear: 0, endYear: 0 },
      });
      onClose?.();
    } catch (error) {
      console.error("Error submitting dividend:", error);
      toast({
        title: "ຜິດພາດ",
        description: "ບໍ່ສາມາດບັນທຶກຂໍ້ມູນໄດ້",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-center text-green-700">
            ແຜນການຊຳລະຈ່າຍຄ່າງວດ {term}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>
              ຈຳນວນເງິນຕົວຈິງ <span className="text-red-500">*</span>
            </Label>
            <Input
              type="text"
              name="dividend"
              placeholder="1,000,000"
              className="text-right rounded-md"
              value={formData.dividend}
              onChange={handleDividendChange}
              inputMode="decimal"
            />
          </div>

          <div className="flex flex-col">
            <DatePicker
              label={
                <p>
                  ວັນທີເດືອນປີຈ່າຍ <span className="text-red-500">*</span>
                </p>
              }
              value={formData.dividendDate}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  dividendDate: value,
                }))
              }
            />
          </div>

          <div className="space-y-2">
            <FileUpload
              currentFile={formData.docFile}
              onFileUpload={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  docFile: value,
                }))
              }
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              ຍົກເລີກ
            </Button>
            <Button
              disabled={isSubmitting}
              type="submit"
              className="text-white bg-green-700"
            >
              {isSubmitting ? <Spinner className="text-white" /> : "ບັນທຶກ"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AssignTaxObligationsEdit: React.FC<AssignTaxObligationProps> = ({
  yearly,
  currentExec,
  dateUpdated,
}) => {
  const navigate = useNavigate();

  const isExecute = currentExec !== 0;

  const [open, setOpen] = useState(false);

  const daysFromNow = calculateDaysFromNow(dateUpdated);

  return (
    <div>
      {isExecute ? (
        <>
          {daysFromNow <= 3 ? (
            <Button
              onClick={() => {
                //localStorage.setItem("companyId", companyId.toString());
                localStorage.setItem("yearly", yearly.toString());
                navigate({
                  to: "/report/obligation/assign-tax-obligation/edit",
                });
              }}
              variant="secondary"
              className="flex items-center border border-blue-500 bg-none gap-x-1 w-[120px] rounded-md p-2 text-blue-500"
            >
              <PencilLine size={16} /> ແກ້ໄຂ
            </Button>
          ) : (
            <Button
              onClick={() => {
                setOpen(true);
              }}
              variant="secondary"
              className="flex items-center border border-blue-500 bg-none gap-x-1 w-[120px] rounded-md p-2 text-blue-500"
            >
              <PencilLine size={16} /> ຂໍແກ້ໄຂເອກະສານ
            </Button>
          )}
          <ModalRequireEditDoc
            isOpen={open}
            onClose={() => setOpen(false)}
            onConfirm={({ reason, file }) => {
              console.log("reason", reason);
              console.log("file", file);
            }}
          />
        </>
      ) : (
        <Button
          onClick={() => {
            //localStorage.setItem("companyId", companyId.toString());

            localStorage.setItem("yearly", yearly.toString());
            navigate({
              to: "/report/obligation/assign-tax-obligation/add-new",
            });
          }}
          variant="secondary"
          className="flex items-center border border-blue-500 bg-none gap-x-1 w-[120px] rounded-md p-2 text-blue-500"
        >
          <CirclePlus size={16} /> ເພີ່ມອາກອນ
        </Button>
      )}
    </div>
  );
};

interface EditInfoProps {
  term: number;
  divId: string;
  open?: boolean;
  onClose?: () => void;
}
interface AssignTaxObligationProps {
  companyId: string;
  yearly: number;
  currentExec: number;
  dateUpdated: string;
}

export { AssignTaxObligations, Dividends };
